import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { erpApi, type ErpAgent, type ErpPendingEarnings, type ErpPayout } from '../../api/erp';
import { formatMoney } from '../../utils/numbers';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, Select } from '../../components/ui/Input';
import { GeneratePasswordAction } from '../../components/ui/GeneratePasswordAction';
import { formatProfessionalRoleRates } from '../../config/support-pricing';
import './erp.css';

export function ErpAgentDetailPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const id = Number(agentId);
  const [agent, setAgent] = useState<ErpAgent | null>(null);
  const [recentTickets, setRecentTickets] = useState<Array<Record<string, unknown>>>([]);
  const [recentSales, setRecentSales] = useState<Array<Record<string, unknown>>>([]);
  const [payouts, setPayouts] = useState<ErpPayout[]>([]);
  const [pendingEarnings, setPendingEarnings] = useState<ErpPendingEarnings | null>(null);
  const [pendingSales, setPendingSales] = useState<Array<Record<string, unknown>>>([]);
  const [form, setForm] = useState({
    status: 'active',
    current_level: 'bronze',
    professional_role: 'technician',
    availability_status: 'offline',
    tax_id: '',
    billing_name: '',
    billing_address: '',
    reset_password: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingPayout, setCreatingPayout] = useState(false);
  const [verifyingSaleId, setVerifyingSaleId] = useState<number | null>(null);

  useEffect(() => {
    if (!id) return;
    erpApi
      .getAgent(id)
      .then((res) => {
        setAgent(res.agent);
        setRecentTickets(res.recentTickets);
        setRecentSales(res.recentSales);
        setPayouts(res.payouts);
        setPendingEarnings(res.pendingEarnings);
        setPendingSales(
          res.recentSales.filter((sale) => String(sale.status) === 'pending')
        );
        setForm({
          status: res.agent.status,
          current_level: res.agent.current_level,
          professional_role: String((res.agent as ErpAgent & { professional_role?: string }).professional_role ?? 'technician'),
          availability_status: res.agent.availability_status,
          tax_id: res.agent.tax_id ?? '',
          billing_name: res.agent.billing_name ?? '',
          billing_address: res.agent.billing_address ?? '',
          reset_password: '',
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await erpApi.updateAgent(id, {
        status: form.status,
        current_level: form.current_level,
        professional_role: form.professional_role,
        availability_status: form.availability_status,
        tax_id: form.tax_id || null,
        billing_name: form.billing_name || null,
        billing_address: form.billing_address || null,
        reset_password: form.reset_password || undefined,
      });
      setSuccess('Cambios guardados');
      setForm((p) => ({ ...p, reset_password: '' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  }

  async function handleVerifySale(saleId: number) {
    setVerifyingSaleId(saleId);
    setError('');
    try {
      await erpApi.verifySale(saleId);
      const res = await erpApi.getAgent(id);
      setRecentSales(res.recentSales);
      setPendingEarnings(res.pendingEarnings);
      setPendingSales(res.recentSales.filter((sale) => String(sale.status) === 'pending'));
      setSuccess('Venta verificada');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo verificar la venta');
    } finally {
      setVerifyingSaleId(null);
    }
  }

  async function handleCreatePayout() {
    setCreatingPayout(true);
    setError('');
    setSuccess('');
    try {
      const result = await erpApi.createPayout(id);
      const res = await erpApi.getAgent(id);
      setPayouts(res.payouts);
      setPendingEarnings(res.pendingEarnings);
      setSuccess(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la liquidación');
    } finally {
      setCreatingPayout(false);
    }
  }

  if (loading) return <p>Cargando agente…</p>;
  if (!agent) return <p>Agente no encontrado</p>;

  return (
    <div>
      <p style={{ marginBottom: '1rem' }}>
        <Link to="/erp/agentes">← Volver a agentes</Link>
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <h1 style={{ margin: 0 }}>
          {agent.name} {agent.surname}
        </h1>
        <Badge>{agent.status}</Badge>
      </div>

      <div className="erp-detail-grid">
        <Card title="Cuenta y facturación">
          <form className="erp-form-grid" onSubmit={handleSave}>
            <p className="muted" style={{ margin: 0 }}>
              {agent.email}
            </p>
            <Select
              label="Estado de la cuenta"
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
              options={[
                { value: 'active', label: 'Activo' },
                { value: 'inactive', label: 'Inactivo' },
                { value: 'suspended', label: 'Suspendido' },
              ]}
            />
            <Select
              label="Nivel"
              value={form.current_level}
              onChange={(e) => setForm((p) => ({ ...p, current_level: e.target.value }))}
              options={[
                { value: 'bronze', label: 'Bronce' },
                { value: 'silver', label: 'Plata' },
                { value: 'gold', label: 'Oro' },
                { value: 'diamond', label: 'Diamante' },
              ]}
            />
            <Select
              label="Rol profesional (tarifa soporte)"
              value={form.professional_role}
              onChange={(e) => setForm((p) => ({ ...p, professional_role: e.target.value }))}
              options={[
                { value: 'technician', label: `Técnico (${formatProfessionalRoleRates('technician')})` },
                { value: 'teamLead', label: `Team Lead (${formatProfessionalRoleRates('teamLead')})` },
              ]}
            />
            <Input
              label="NIF/CIF"
              value={form.tax_id}
              onChange={(e) => setForm((p) => ({ ...p, tax_id: e.target.value }))}
            />
            <Input
              label="Nombre facturación"
              value={form.billing_name}
              onChange={(e) => setForm((p) => ({ ...p, billing_name: e.target.value }))}
            />
            <label className="ui-field">
              <span className="ui-field__label">Dirección facturación</span>
              <textarea
                className="ui-textarea"
                rows={3}
                value={form.billing_address}
                onChange={(e) => setForm((p) => ({ ...p, billing_address: e.target.value }))}
              />
            </label>
            <Input
              label="Nueva contraseña (opcional)"
              type="password"
              value={form.reset_password}
              onChange={(e) => setForm((p) => ({ ...p, reset_password: e.target.value }))}
              showPasswordToggle
              labelAction={
                <GeneratePasswordAction
                  disabled={saving}
                  onGenerate={(pwd) => setForm((p) => ({ ...p, reset_password: pwd }))}
                />
              }
            />
            {error ? <p style={{ color: 'var(--color-danger)', margin: 0 }}>{error}</p> : null}
            {success ? <p style={{ color: 'var(--color-success)', margin: 0 }}>{success}</p> : null}
            <Button type="submit" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </form>
        </Card>

        <Card title="Actividad">
          <div className="erp-stats" style={{ marginBottom: 0 }}>
            <div>
              <span className="muted">Tickets resueltos</span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', fontWeight: 700 }}>
                {agent.total_tickets_resolved}
              </p>
            </div>
            <div>
              <span className="muted">Ventas</span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', fontWeight: 700 }}>
                {agent.total_sales_made}
              </p>
            </div>
            <div>
              <span className="muted">Ingresos acumulados</span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', fontWeight: 700 }}>
                {Number(agent.total_earnings).toFixed(2)} €
              </p>
            </div>
          </div>
          <p className="muted" style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
            Alta: {new Date(agent.joined_at).toLocaleDateString('es-ES')}
            {agent.referral_code ? ` · Código: ${agent.referral_code}` : ''}
          </p>
        </Card>
      </div>

      {pendingEarnings ? (
        <div style={{ marginTop: '1.25rem' }}>
          <Card title="Ganancias pendientes de liquidar">
            <div className="erp-stats" style={{ marginBottom: '1rem' }}>
              <div>
                <span className="muted">Soporte</span>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700 }}>{formatMoney(pendingEarnings.tickets)}</p>
              </div>
              <div>
                <span className="muted">Valoraciones</span>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700 }}>{formatMoney(pendingEarnings.rating_bonus)}</p>
              </div>
              <div>
                <span className="muted">Ventas verificadas</span>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700 }}>{formatMoney(pendingEarnings.sales_commission)}</p>
              </div>
              {(pendingEarnings.referral_milestone_bonus ?? 0) > 0 ? (
                <div>
                  <span className="muted">Hitos de referidos</span>
                  <p style={{ margin: '0.25rem 0 0', fontWeight: 700 }}>
                    {formatMoney(pendingEarnings.referral_milestone_bonus)}
                  </p>
                </div>
              ) : null}
              <div>
                <span className="muted">Total pendiente</span>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700, color: 'var(--color-primary)' }}>
                  {formatMoney(pendingEarnings.total)}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Button onClick={() => void handleCreatePayout()} disabled={creatingPayout || pendingEarnings.total <= 0}>
                {creatingPayout ? 'Creando liquidación…' : 'Crear liquidación'}
              </Button>
              <Link to="/erp/pagos">
                <Button variant="ghost">Ver todas las liquidaciones</Button>
              </Link>
            </div>
          </Card>
        </div>
      ) : null}

      <div className="erp-detail-grid" style={{ marginTop: '1.25rem' }}>
        <Card title="Últimos tickets">
          {recentTickets.length === 0 ? (
            <p className="muted">Sin tickets recientes</p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {recentTickets.map((t) => (
                <li key={String(t.id)} style={{ marginBottom: '0.35rem' }}>
                  #{String(t.id)} · {String(t.status)} · {String(t.subject ?? t.customer_name ?? '—')}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Últimas ventas">
          {recentSales.length === 0 ? (
            <p className="muted">Sin ventas registradas</p>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {recentSales.map((s) => (
                <li
                  key={String(s.id)}
                  style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    {Number(s.sale_amount).toFixed(2)} € · {String(s.status)} · {String(s.sale_description)}
                  </span>
                  {String(s.status) === 'pending' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={verifyingSaleId === Number(s.id)}
                      onClick={() => void handleVerifySale(Number(s.id))}
                    >
                      {verifyingSaleId === Number(s.id) ? 'Verificando…' : 'Verificar'}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {pendingSales.length > 0 ? (
            <p className="muted" style={{ margin: '0.75rem 0 0', fontSize: '0.85rem' }}>
              {pendingSales.length} venta(s) pendiente(s) de verificación antes de liquidar.
            </p>
          ) : null}
        </Card>
      </div>

      {payouts.length > 0 ? (
        <div style={{ marginTop: '1.25rem' }}>
          <Card title="Liquidaciones del agente">
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {payouts.map((p) => (
                <li
                  key={p.id}
                  style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    {new Date(p.period_start).toLocaleDateString('es-ES')} –{' '}
                    {new Date(p.period_end).toLocaleDateString('es-ES')} · {String(p.status)}
                  </span>
                  <strong>{formatMoney(p.total_amount)}</strong>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
